import { useState } from 'react';
import {
  Bird,
  CheckCircle2,
  Clock,
  History,
  Play,
  Save,
  Settings2,
  XCircle,
} from 'lucide-react';
import {
  useAutoRetryStatus,
  useRunAutoRetryCycle,
  useSaveAutoRetryConfig,
} from '../../hooks/useApi';
import { StatusBadge } from '../shared/StatusBadge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';
import type { AutoRetryEvent, AutoRetryStatus } from '../../types/api';

interface AutoRetryCardProps {
  isAdmin: boolean;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '-' : new Date(ms).toLocaleString('pl-PL');
}

function formatRelative(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '-';
  const diffMin = Math.round((ms - Date.now()) / 60_000);
  if (diffMin <= 0) return 'teraz';
  if (diffMin < 60) return `za ${diffMin} min`;
  const hours = Math.floor(diffMin / 60);
  return `za ${hours}h ${diffMin % 60}min`;
}

const EVENT_LABELS: Record<AutoRetryEvent, { label: string; variant: 'success' | 'danger' | 'warning' | 'info' | 'neutral' }> = {
  'probe-started': { label: 'Sonda', variant: 'info' },
  'probe-success': { label: 'Sukces', variant: 'success' },
  'probe-failed': { label: 'Blad', variant: 'danger' },
  'probe-timeout': { label: 'Timeout', variant: 'warning' },
  'probe-missing': { label: 'Brak danych', variant: 'neutral' },
  error: { label: 'Blad', variant: 'danger' },
};

/** Headline badge — what the canary is doing right now. */
function statusBadge(status: AutoRetryStatus) {
  if (!status.config.enabled) return <StatusBadge label="Wylaczone" variant="neutral" />;
  if (status.state.status === 'probing') return <StatusBadge label="Sonda w toku" variant="info" pulse />;
  if (status.failureCount === 0) return <StatusBadge label="Brak bledow" variant="success" />;
  if (status.state.consecutiveFailures > 0) {
    return <StatusBadge label={`Czeka (${status.state.consecutiveFailures} nieudanych sond)`} variant="warning" />;
  }
  return <StatusBadge label="Uzbrojone" variant="success" />;
}

export function AutoRetryCard({ isAdmin }: AutoRetryCardProps) {
  const status = useAutoRetryStatus();
  const saveConfig = useSaveAutoRetryConfig();
  const runCycle = useRunAutoRetryCycle();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ intervalMinutes: 60, probeTimeoutMinutes: 20, lookbackDays: 7, minFailures: 3 });
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastRunMessage, setLastRunMessage] = useState<string | null>(null);

  const data = status.data;

  const startEditing = () => {
    if (!data) return;
    setForm({
      intervalMinutes: data.config.intervalMinutes,
      probeTimeoutMinutes: data.config.probeTimeoutMinutes,
      lookbackDays: data.config.lookbackDays,
      minFailures: data.config.minFailures,
    });
    setEditing(true);
  };

  const handleToggle = async () => {
    if (!data) return;
    setActionError(null);
    try {
      await saveConfig.mutateAsync({ enabled: !data.config.enabled });
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleSave = async () => {
    setActionError(null);
    try {
      await saveConfig.mutateAsync(form);
      setEditing(false);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleRunNow = async () => {
    setActionError(null);
    setLastRunMessage(null);
    try {
      const result = await runCycle.mutateAsync();
      setLastRunMessage(result.message);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Bird className="h-4 w-4 text-brand-500" />
            Auto-ponawianie (kanarek)
            <span className="badge-info">PROD</span>
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Co {data?.config.intervalMinutes ?? 60} min ponawia 1 bledne poswiadczenie. Jesli przejdzie —
            automatycznie ponawia pozostale.
          </p>
        </div>
        {data && <div className="shrink-0">{statusBadge(data)}</div>}
      </div>

      {status.isLoading ? (
        <LoadingSpinner size="sm" />
      ) : status.error ? (
        <ErrorAlert message={status.error.message} onRetry={() => status.refetch()} />
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Bledy trwale (PROD, {data.config.lookbackDays} dni)</p>
              <p className={`text-lg font-bold ${data.failureCount > 0 ? 'text-red-600 dark:text-red-300' : 'text-gray-400 dark:text-gray-600'}`}>
                {data.failureCount}
              </p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Nastepna sonda
              </p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mt-1">
                {data.config.enabled ? formatRelative(data.state.nextProbeAt) : 'wylaczone'}
              </p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Ostatnia sonda</p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mt-1 flex items-center gap-1">
                {data.state.lastOutcome === 'success' ? (
                  <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> sukces</>
                ) : data.state.lastOutcome ? (
                  <><XCircle className="h-3.5 w-3.5 text-red-500" /> {data.state.lastOutcome}</>
                ) : (
                  '-'
                )}
              </p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Ostatnie odzyskanie</p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mt-1">
                {data.state.lastRecoveryAt ? `${data.state.lastRecoveryCount} szt.` : '-'}
              </p>
              {data.state.lastRecoveryAt && (
                <p className="text-[11px] text-gray-500">{formatDateTime(data.state.lastRecoveryAt)}</p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-4">{data.gateMessage}</p>

          {data.state.status === 'probing' && data.state.probeCredentialId && (
            <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 p-3 text-xs text-blue-700 dark:text-blue-300">
              Sonduje poswiadczenie <span className="font-mono">{data.state.probeCredentialId.substring(0, 8)}...</span>
              {data.state.probeOrganisationName ? ` (${data.state.probeOrganisationName})` : ''}, start:{' '}
              {formatDateTime(data.state.probeStartedAt)}
            </div>
          )}

          {data.state.lastError && data.state.lastOutcome !== 'success' && (
            <p className="text-xs text-red-500 dark:text-red-300/80 mb-4 truncate" title={data.state.lastError}>
              Ostatni blad sondy: {data.state.lastError}
            </p>
          )}

          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button
                onClick={handleToggle}
                disabled={saveConfig.isPending}
                className={`${data.config.enabled ? 'btn-secondary' : 'btn-primary'} text-sm disabled:opacity-50`}
              >
                {data.config.enabled ? 'Wylacz' : 'Wlacz'}
              </button>
              <button
                onClick={handleRunNow}
                disabled={runCycle.isPending || data.state.status === 'probing'}
                className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
                title={data.state.status === 'probing' ? 'Sonda jest juz w toku' : 'Wypusc sonde teraz'}
              >
                <Play className="h-3.5 w-3.5" />
                {runCycle.isPending ? 'Uruchamiam...' : 'Uruchom teraz'}
              </button>
              <button
                onClick={editing ? () => setEditing(false) : startEditing}
                className="btn-secondary text-sm flex items-center gap-2"
              >
                <Settings2 className="h-3.5 w-3.5" />
                {editing ? 'Anuluj' : 'Ustawienia'}
              </button>
            </div>
          )}

          {lastRunMessage && (
            <p className="text-xs text-emerald-600 dark:text-emerald-300 mb-4">{lastRunMessage}</p>
          )}
          {actionError && <ErrorAlert message={actionError} />}

          {isAdmin && editing && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <label className="text-xs text-gray-500">
                Co ile minut sonda
                <input
                  type="number"
                  min={5}
                  max={1440}
                  value={form.intervalMinutes}
                  onChange={(e) => setForm({ ...form, intervalMinutes: Number(e.target.value) })}
                  className="input-field text-sm w-full mt-1"
                />
              </label>
              <label className="text-xs text-gray-500">
                Limit czasu sondy (min)
                <input
                  type="number"
                  min={5}
                  max={240}
                  value={form.probeTimeoutMinutes}
                  onChange={(e) => setForm({ ...form, probeTimeoutMinutes: Number(e.target.value) })}
                  className="input-field text-sm w-full mt-1"
                />
              </label>
              <label className="text-xs text-gray-500">
                Okno bledow (dni)
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={form.lookbackDays}
                  onChange={(e) => setForm({ ...form, lookbackDays: Number(e.target.value) })}
                  className="input-field text-sm w-full mt-1"
                />
              </label>
              <label className="text-xs text-gray-500">
                Min. bledow by ruszyc
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={form.minFailures}
                  onChange={(e) => setForm({ ...form, minFailures: Number(e.target.value) })}
                  className="input-field text-sm w-full mt-1"
                />
              </label>
              <div className="md:col-span-4">
                <button
                  onClick={handleSave}
                  disabled={saveConfig.isPending}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="h-3.5 w-3.5" />
                  Zapisz
                </button>
              </div>
            </div>
          )}

          {data.history.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1.5 mb-2">
                <History className="h-3.5 w-3.5" />
                Historia sond
              </h3>
              <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                {data.history.map((entry) => {
                  const meta = EVENT_LABELS[entry.event] ?? EVENT_LABELS.error;
                  return (
                    <li key={entry.id} className="flex items-start gap-2 text-xs">
                      <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap tabular-nums">
                        {formatDateTime(entry.at)}
                      </span>
                      <StatusBadge label={meta.label} variant={meta.variant} />
                      <span className="text-gray-600 dark:text-gray-300 flex-1">{entry.message}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
